import { Observable, Subject } from 'rxjs';
import {
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';

import CheckCircleIcon from 'assets/icons/check-circle.svg';
import Organization from 'entities/organization/model';
import OrganizationApi, { NonprofitSearchResult } from 'entities/organization/api';
import FormFieldLabel from 'retired/FormFieldLabel';
import LocationSelector from 'retired/LocationSelector';
import SelectableChip from 'retired/SelectableChip';
import makeSortableItem from 'retired/SortableItem';
import NonprofitLink from 'retired/NonprofitLink';
import Img from 'retired/Img';
import { computeDonationProviderForNonprofit } from 'utils/computeDonationProviderForNonprofit';
import { selectUser } from 'entities/user/selectors';
import { renderAddress } from 'utils/renderAddress';
import { getDonationProviderDisplayName } from 'utils/getDonationProviderDisplayName';

import SelectedOption from './SelectedOption';

const ConnectedSelectedOption = makeSortableItem({
  itemType: 'content',
  wrapperStyle: {
    position: 'relative',
    maxWidth: 1000,
    paddingTop: 6,
    paddingBottom: 2,
    padding: 0,
    marginBottom: 10,
  },
  overlayStyle: {
    top: -7,
    bottom: -7,
  },
})(({ nonprofit, onRemove }: { nonprofit: Organization; onRemove: () => void }) => (
  <SelectedOption
    key={nonprofit.get('id')}
    nonprofit={nonprofit}
    remove={onRemove}
    isEditable={false}
  />
));

// this type should match the intersection of NonprofitSearchResult and community['Nonprofits']
export type NonprofitOption = {
  id: string;
  name: string;
  locationObject?: {
    city?: string;
    address?: string;
    postalCode?: string;
    state?: string;
  };
};

type NpoSearchArgs = [string, string, boolean, string | undefined];

const arrayMove = <T,>(arr: T[], fromIndex: number, toIndex: number): T[] => {
  const newArray = [...arr];
  const element = newArray[fromIndex];
  newArray.splice(fromIndex, 1);
  newArray.splice(toIndex, 0, element);
  return newArray;
};

export const Option = React.memo(
  ({ nonprofit, add }: { nonprofit: NonprofitSearchResult; add: () => void }) => {
    const location = renderAddress(nonprofit.locationObject);

    const user = useSelector(selectUser);
    const preferStripe = user?.organization?.settings?.get('preferStripe') || false;
    const providerCode = computeDonationProviderForNonprofit(
      new Organization(nonprofit),
      preferStripe
    );
    const providerName = getDonationProviderDisplayName(providerCode);
    const showPoweredBy = user?.isAdmin || false;
    return (
      <ListItem button onClick={() => add()} title="Select nonprofit">
        <AddIcon />
        <ListItemText
          primary={
            <>
              <NonprofitLink nonprofit={nonprofit} />{' '}
              {nonprofit.ein ? <>– EIN: {nonprofit.ein}</> : null}
              {nonprofit.approved && (
                <Img
                  src={CheckCircleIcon}
                  style={{ width: '20px', marginLeft: 6 }}
                  alt="Approved"
                  title="Approved"
                />
              )}
            </>
          }
          secondary={
            <>
              {location}
              {showPoweredBy && providerName && (
                <>
                  <br />
                  Powered by: {providerName}
                </>
              )}
            </>
          }
        />
      </ListItem>
    );
  }
);

const useNonprofitSearch = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<NonprofitSearchResult[]>([]);
  const [onSearch$] = useState(() => new Subject<NpoSearchArgs>());

  const user = useSelector(selectUser);
  const searchNonprofitDB = user?.organization?.settings?.get('searchNonprofitDB') || false;

  useEffect(() => {
    const subscription = onSearch$
      .pipe(
        map(([s, l, x, z]): NpoSearchArgs => [s.trim(), l, x, z]),
        debounceTime(600),
        switchMap(([searchTerm, location, acceptsDonations, donationProvider]) => {
          if (searchTerm.length <= 2) {
            return Observable.of([]);
          }
          setLoading(true);
          return OrganizationApi.searchNonprofits(
            {
              searchTerm,
              countryCode: location,
              acceptsDonations,
              donationProvider,
            },
            searchNonprofitDB
          );
        })
      )
      .subscribe((results) => {
        setLoading(false);
        setData(results);
      });
    return () => {
      subscription.unsubscribe();
      onSearch$.unsubscribe();
    };
  }, [onSearch$]);

  return {
    loading,
    data,
    searchNonprofits: (
      search: string,
      location: string,
      acceptsDonations: boolean,
      donationProvider?: string
    ) => onSearch$.next([search, location, acceptsDonations, donationProvider]),
  };
};

export const NonprofitsSelector = ({
  value,
  onChange,
  initialLocation = 'US',
  limit = Infinity,
  required = false,
  donationProvider,
}: {
  value: NonprofitOption[];
  onChange: (value: NonprofitOption[]) => void;
  initialLocation?: string;
  required?: boolean;
  limit?: number;
  donationProvider?: string;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string>(initialLocation);
  const { searchNonprofits, data, loading } = useNonprofitSearch();
  const onChangeSearch = (v: string) => {
    searchNonprofits(v, selectedLocation, true, donationProvider);
    setSearchTerm(v);
  };

  return (
    <Grid container spacing={3}>
      <Grid item md={6}>
        <LocationSelector
          onSelect={(x) => setSelectedLocation(x)}
          selectedLocation={selectedLocation}
        />
        <Grid container justifyContent="space-between">
          <Grid
            item
            style={{
              width: '80%',
              marginTop: 16,
            }}
          >
            <FormControl
              style={{
                width: '100%',
                height: 17,
              }}
            >
              <InputLabel shrink>
                {limit < 2 ? 'Select a nonprofit' : 'Select your nonprofits'}
                {required ? '*' : ''}
              </InputLabel>
            </FormControl>
          </Grid>
          <Grid item>
            <SelectableChip onClick={() => onChange([])} label="Unset" style={{ margin: 0 }} />
          </Grid>
        </Grid>
        <TextField
          placeholder="Search by nonprofit name or EIN"
          disabled={!selectedLocation}
          value={searchTerm}
          onChange={(e) => onChangeSearch(e.target.value)}
          style={{ width: '100%' }}
        />
        <List
          style={{
            maxHeight: 450,
            overflow: 'auto',
          }}
        >
          {loading ? (
            <div
              style={{
                textAlign: 'center',
                marginTop: 20,
                marginBottom: 20,
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            data
              .map((item) => {
                const selected = value.find((i) => i.id === item.id);
                const add = () => {
                  if (value.length === limit) {
                    // eslint-disable-next-line no-alert
                    alert(
                      limit > 1
                        ? `You cannot select more than ${limit} nonprofits`
                        : `You can only select a single nonprofit`
                    );
                  } else if (limit < 2) {
                    onChange([item]);
                  } else {
                    onChange([...value, item]);
                  }
                };
                return !selected && <Option key={item.id} add={add} nonprofit={item} />;
              })
              .filter(Boolean)
          )}
        </List>
      </Grid>
      <Grid item md={6}>
        <FormFieldLabel>
          {`Selected nonprofit${limit > 1 ? 's' : ''}${required ? ' *' : ''}`}
        </FormFieldLabel>
        <List
          style={{
            maxHeight: 560,
            overflow: 'auto',
          }}
        >
          {value.map((nonprofit, index) => (
            <ConnectedSelectedOption
              key={nonprofit.id}
              index={index}
              nonprofit={new Organization(nonprofit)}
              onRemove={() => onChange(value.filter((n) => n.id !== nonprofit.id))}
              move={(fromIndex: number, toIndex: number) =>
                onChange(arrayMove(value, fromIndex, toIndex))
              }
            />
          ))}
        </List>
      </Grid>
    </Grid>
  );
};
